import { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft, Send, User, Wrench, Shield,
  X, Package, Building2, Calendar, CreditCard
} from "lucide-react";
import axios from "axios";
import SupStatusBadge from "../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupLoader from "../../../components/general/SupportSharedFiles/SupLoader";
import "../../../assets/css/general/Support/supshare.css";
import "../../../assets/css/customer/customerSupport/cussupportdetail.css";
import AppContext from "../../../context/AppContext";

const STATUS_STEPS = ["open", "in_progress", "resolved"];

const formatDate = (dateStr) => {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-PK", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
};

const CusSupportDetail = () => {
  const { id } = useParams();
  const { baseUrl, authToken } = useContext(AppContext);
  const navigate = useNavigate();
  const bottomRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [reply, setReply] = useState("");

  const fetchDetail = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/customer/support/tickets/${id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        const t = res.data.ticket;
        setTicket(t);
        const chats = t?.jjconversations?.[0]?.jjchats || [];
        setMessages(chats);
        setError("");
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        setError("Failed to load ticket details.");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchDetail(); }, [id, authToken]);
  useEffect(() => {
    const pollMessages = () => {
      axios
        .get(`${baseUrl}/customer/support/tickets/${id}`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => {
          const chats = res.data.ticket?.jjconversations?.[0]?.jjchats || [];
          setMessages(chats);
          setTicket((prev) =>
            prev
              ? {
                  ...prev,
                  status: res.data.ticket.status,
                  favour_in: res.data.ticket.favour_in,
                }
              : prev
          );
        })
        .catch(() => {});
    };
    const interval = setInterval(pollMessages, 5000);
    return () => clearInterval(interval);
  }, [id, authToken]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const sendMessage = () => {
    if (!reply.trim()) return;
    setSending(true);

    axios
      .post(
        `${baseUrl}/customer/support/tickets/${id}/messages`,
        { message: reply },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setMessages((prev) => [...prev, res.data.chat]);
        setReply("");
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        alert("Failed to send message.");
      })
      .finally(() => setSending(false));
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getSenderIcon = (sender) => {
    if (sender === "admin")  return <Shield size={13} />;
    if (sender === "vendor") return <Wrench size={13} />;
    return <User size={13} />;
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-PK", {
      hour: "2-digit", minute: "2-digit",
    });
  };

  const stepIndex = STATUS_STEPS.indexOf(ticket?.status);

  if (loading) return <div className="cus-sdt-page"><SupLoader label="Loading ticket..." /></div>;

  if (!ticket && !loading) {
    return (
      <div className="cus-sdt-page">
        <div className="sup-error-banner">{error || "Ticket not found."}</div>
        <button className="cus-sdt-back-btn" onClick={() => navigate(-1)}>
          <ArrowLeft size={15} /> Go Back
        </button>
      </div>
    );
  }

  const booking = ticket?.jjbooking;

  return (
    <div className="cus-sdt-page">

      {/* Back Button */}
      <button className="cus-sdt-back-btn" onClick={() => navigate(-1)}>
        <ArrowLeft size={15} /> Back to Tickets
      </button>

      {error && <div className="sup-error-banner">{error}</div>}

      <div className="cus-sdt-layout">

        <div className="cus-sdt-sidebar">

          {/* Ticket Info */}
          <div className="cus-sdt-info-card">
            <div className="cus-sdt-info-card__head">
              <span className="cus-sdt-info-id">#{ticket.id}</span>
              <SupStatusBadge status={ticket.status} />
            </div>
            <h3 className="cus-sdt-info-title">{ticket.category}</h3>
            <p className="cus-sdt-info-desc">{ticket.description}</p>
            <div className="cus-sdt-info-meta">
              <span>Raised by: <strong>{ticket.raisedby}</strong></span>
              <span>Against: <strong>{ticket.against}</strong></span>
            </div>
          </div>

          {/* Booking Details */}
          {booking && (
            <div className="cus-sdt-booking-card">
              <p className="cus-sdt-section-label">Booking Details</p>
              <div className="cus-sdt-booking-rows">
                <div className="cus-sdt-booking-row">
                  <span><Package size={13} /> Type</span>
                  <span className="cus-sdt-booking-val">{booking.booking_type}</span>
                </div>
                <div className="cus-sdt-booking-row">
                  <span><Building2 size={13} /> Vendor</span>
                  <span className="cus-sdt-booking-val">{booking.Jjvendor?.name || "—"}</span>
                </div>
                {booking.Jjpackage && (
                  <div className="cus-sdt-booking-row">
                    <span><Package size={13} /> Package</span>
                    <span className="cus-sdt-booking-val">{booking.Jjpackage.package_name}</span>
                  </div>
                )}
                <div className="cus-sdt-booking-row">
                  <span><Calendar size={13} /> Check-In</span>
                  <span className="cus-sdt-booking-val">{formatDate(booking.checkIn)}</span>
                </div>
                <div className="cus-sdt-booking-row">
                  <span><Calendar size={13} /> Check-Out</span>
                  <span className="cus-sdt-booking-val">{formatDate(booking.checkOut)}</span>
                </div>
                <div className="cus-sdt-booking-row">
                  <span><CreditCard size={13} /> Amount</span>
                  <span className="cus-sdt-booking-val">
                    PKR {Number(booking.total_amount || 0).toLocaleString()}
                  </span>
                </div>
                <div className="cus-sdt-booking-row">
                  <span>Status</span>
                  <span className="cus-sdt-booking-val cus-sdt-booking-val--cap">
                    {booking.booking_status}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="cus-sdt-right">

          <div className="cus-sdt-timeline-card">
            <p className="cus-sdt-section-label">Status Timeline</p>
            {STATUS_STEPS.map((step, i) => (
              <div
                key={step}
                className={`cus-sdt-step ${i <= stepIndex ? "cus-sdt-step--done" : ""}`}
              >
                <div className="cus-sdt-step__dot" />
                {i < STATUS_STEPS.length - 1 && <div className="cus-sdt-step__line" />}
                <span className="cus-sdt-step__label">{step.replace("_", " ")}</span>
              </div>
            ))}
            {ticket.favour_in && (
              <p className="cus-sdt-resolved-note">
                Resolved in favor of: <strong>{ticket.favour_in}</strong>
              </p>
            )}
          </div>

          <div className="cus-sdt-chat">
            <div className="cus-sdt-chat__header">
              <p className="cus-sdt-chat__title">Conversation</p>
              <span className="cus-sdt-chat__count">{messages.length} messages</span>
            </div>
            <div className="cus-sdt-chat__messages">
              {messages.length === 0 && (
                <div className="cus-sdt-chat__empty">
                  No messages yet. Start the conversation below.
                </div>
              )}
              {messages.map((msg, idx) => (
                <div
                  key={msg.id || idx}
                  className={`cus-sdt-msg ${
                    msg.sender === "customer" ? "cus-sdt-msg--self" : "cus-sdt-msg--other"
                  }`}
                >
                  {msg.sender !== "customer" && (
                    <div className={`cus-sdt-msg__avatar cus-sdt-msg__avatar--${msg.sender}`}>
                      {getSenderIcon(msg.sender)}
                    </div>
                  )}
                  <div className="cus-sdt-msg__bubble">
                    <p className="cus-sdt-msg__sender">{msg.sender}</p>
                    {msg.message && <p className="cus-sdt-msg__text">{msg.message}</p>}
                    <span className="cus-sdt-msg__time">{formatTime(msg.created_at)}</span>
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>

            {ticket.status !== "resolved" && (
              <div className="cus-sdt-chat__footer">
                <div className="cus-sdt-chat__input-row">
                  <textarea
                    className="cus-sdt-reply-input"
                    value={reply}
                    onChange={(e) => setReply(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type your message… (Enter to send)"
                    rows={1}
                  />
                  <button
                    className="cus-sdt-send-btn"
                    onClick={sendMessage}
                    disabled={sending || !reply.trim()}
                  >
                    <Send size={15} />
                  </button>
                </div>
              </div>
            )}

            {ticket.status === "resolved" && (
              <div className="cus-sdt-resolved-footer">
                ✓ This ticket has been resolved
                {ticket.favour_in && (
                  <span className="cus-sdt-favour-tag">
                    · Favor: <strong>{ticket.favour_in}</strong>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CusSupportDetail;